// src/pages/buyer/BuyerTierPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { buyerTierApi } from '@/api/services'
import { Crown, Check, ArrowRight, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

const tierIcons = {
  individual: '👤',
  designer:   '🎨',
  brand:      '🏷️',
  enterprise: '🏢',
}

const tierColors = {
  individual: 'border-gray-200 bg-gray-50',
  designer:   'border-purple-300 bg-purple-50',
  brand:      'border-pink-300 bg-pink-50',
  enterprise: 'border-amber-300 bg-amber-50',
}

const tierAccent = {
  individual: 'bg-gray-500',
  designer:   'bg-purple-500',
  brand:      'bg-pink-500',
  enterprise: 'bg-amber-600',
}

export default function BuyerTierPage() {
  const qc = useQueryClient()
  const [selectedTier, setSelectedTier] = useState(null)
  const [form, setForm] = useState({ company_type: '', annual_volume: '', specialities: '', business_name: '', gstin: '' })

  const { data, isLoading } = useQuery({
    queryKey: ['buyer-tier'],
    queryFn: () => buyerTierApi.show().then(r => r.data),
  })

  const upgrade = useMutation({
    mutationFn: (d) => buyerTierApi.upgrade(d),
    onSuccess: (res) => {
      toast.success(res.data.message)
      qc.invalidateQueries({ queryKey: ['buyer-tier'] })
      setSelectedTier(null)
      setForm({ company_type: '', annual_volume: '', specialities: '', business_name: '', gstin: '' })
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Upgrade failed'),
  })

  if (isLoading) return <div className="text-center py-12 text-gray-400">Loading...</div>

  const info = data?.data || {}
  const currentTier = info.current_tier || 'individual'
  const tiers = info.tiers || []
  const tierOrder = ['individual', 'designer', 'brand', 'enterprise']
  const currentIdx = tierOrder.indexOf(currentTier)

  function handleSubmit(e) {
    e.preventDefault()
    upgrade.mutate({ tier: selectedTier, ...form })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Crown className="w-6 h-6 text-pink-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Buyer Tier</h1>
          <p className="text-xs text-gray-500">
            Current tier: <span className="font-bold capitalize">{currentTier}</span>
            {info.tier_verified ? ' (Verified)' : currentTier !== 'individual' ? ' (Pending verification)' : ''}
          </p>
        </div>
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiers.map((tier) => {
          const isActive = tier.key === currentTier
          const tierIdx = tierOrder.indexOf(tier.key)
          const canUpgrade = tierIdx > currentIdx && tier.key !== 'individual'

          return (
            <div key={tier.key}
              className={`rounded-xl border-2 p-5 transition-all ${
                isActive ? `${tierColors[tier.key]} ring-2 ring-offset-2 ring-pink-400` : tierColors[tier.key]
              }`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">{tierIcons[tier.key]}</span>
                <div>
                  <h3 className="font-bold text-gray-900">{tier.name}</h3>
                  {isActive && (
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${tierAccent[tier.key]}`}>
                      CURRENT
                    </span>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-600 mb-3">{tier.description}</p>

              <ul className="space-y-1.5 mb-4">
                {tier.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                    <Check className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>

              {tier.requirements && (
                <p className="text-[10px] text-gray-500 mb-3 italic">Requires: {tier.requirements}</p>
              )}

              {canUpgrade && (
                <button
                  onClick={() => setSelectedTier(tier.key)}
                  className="w-full py-2 rounded-lg text-xs font-bold text-white bg-pink-500 hover:bg-pink-600 transition flex items-center justify-center gap-1">
                  Upgrade <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )
        })}
      </div>

      {/* Upgrade form */}
      {selectedTier && (
        <div className="bg-white rounded-xl border p-6">
          <h2 className="font-bold text-lg text-gray-900 mb-1">
            Upgrade to {selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)}
          </h2>
          <p className="text-xs text-gray-500 mb-4">
            {selectedTier === 'enterprise'
              ? 'Enterprise upgrades require admin approval. We\'ll review your application within 24 hours.'
              : 'Fill in your details to activate your new tier instantly.'}
          </p>

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Company Type *</label>
              <select value={form.company_type} onChange={e => setForm(f => ({ ...f, company_type: e.target.value }))}
                required className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Select...</option>
                <option value="design_studio">Design Studio</option>
                <option value="fashion_label">Fashion Label</option>
                <option value="d2c_brand">D2C Brand</option>
                <option value="garment_exporter">Garment Exporter</option>
                <option value="manufacturer">Manufacturer</option>
                <option value="retail_chain">Retail Chain</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Annual Volume</label>
              <select value={form.annual_volume} onChange={e => setForm(f => ({ ...f, annual_volume: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm">
                <option value="">Select...</option>
                <option value="under_5L">Under ₹5 Lakh</option>
                <option value="5L_25L">₹5L – ₹25 Lakh</option>
                <option value="25L_50L">₹25L – ₹50 Lakh</option>
                <option value="50L_1Cr">₹50L – ₹1 Crore</option>
                <option value="above_1Cr">Above ₹1 Crore</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">Specialities / Fabric Types</label>
              <input type="text" value={form.specialities}
                onChange={e => setForm(f => ({ ...f, specialities: e.target.value }))}
                placeholder="e.g. Silk, Cotton, Khadi, Denim"
                className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>

            {(selectedTier === 'brand' || selectedTier === 'enterprise') && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Business Name</label>
                  <input type="text" value={form.business_name}
                    onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">GSTIN</label>
                  <input type="text" value={form.gstin}
                    onChange={e => setForm(f => ({ ...f, gstin: e.target.value }))}
                    placeholder="15-character GSTIN"
                    maxLength={15}
                    className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </>
            )}

            <div className="md:col-span-2 flex items-center gap-3">
              <button type="submit" disabled={upgrade.isPending}
                className="px-6 py-2.5 rounded-lg text-sm font-bold text-white bg-pink-500 hover:bg-pink-600 transition disabled:opacity-50 flex items-center gap-2">
                {upgrade.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                {selectedTier === 'enterprise' ? 'Submit for Review' : 'Upgrade Now'}
              </button>
              <button type="button" onClick={() => setSelectedTier(null)}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
